import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "modalPanel",
    "step1", "step2",
    "backBtn", "nextBtn",
    "photoGrid", "photoSpinner", "photoCount",
    "meetingFields",
    "tbd"
  ]

  static values = {
    photosUrl: String,
    submitUrl: String
  }

  connect() {
    this._photosLoaded = false
    this._currentPage = 1
    this._isLastPage = false
    this._loadingPhotos = false
    this._pollTimers = []
  }

  disconnect() {
    this._pollTimers.forEach(t => clearTimeout(t))
    this._pollTimers = []
  }

  // ── Modal ─────────────────────────────────────────────────────────

  open(event) {
    event.preventDefault()
    event.stopPropagation()
    document.dispatchEvent(new CustomEvent("dropdown:close"))
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._showStep1()
  }

  hideModal(event) {
    if (event.target === this.modalTarget) {
      this._close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  close() {
    this._close()
  }

  _close() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  // ── Steps ─────────────────────────────────────────────────────────

  _showStep1() {
    this.step1Target.classList.remove("hidden")
    this.step2Target.classList.add("hidden")
    this.backBtnTarget.classList.add("hidden")
    this.nextBtnTarget.textContent = "Next"
  }

  _showStep2() {
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.remove("hidden")
    this.backBtnTarget.classList.remove("hidden")
    this.nextBtnTarget.textContent = "Create"

    if (!this._photosLoaded) {
      this._loadPhotos()
    }
  }

  goBack() {
    this._showStep1()
  }

  nextStep() {
    if (this.step2Target.classList.contains("hidden")) {
      // Currently on step 1 -> go to step 2
      this._showStep2()
    } else {
      // Currently on step 2 -> submit
      this._submit()
    }
  }

  // ── TBD checkbox ──────────────────────────────────────────────────

  toggleTbd() {
    const checked = this.tbdTarget.checked
    const fields = this.meetingFieldsTarget
    if (checked) {
      fields.style.opacity = "0.5"
      fields.style.pointerEvents = "none"
    } else {
      fields.style.opacity = ""
      fields.style.pointerEvents = ""
    }
  }

  // ── Photo loading ─────────────────────────────────────────────────

  async _loadPhotos() {
    if (this._loadingPhotos) return
    this._loadingPhotos = true
    this.photoSpinnerTarget.classList.remove("hidden")

    try {
      const resp = await fetch(`${this.photosUrlValue}.json?page=${this._currentPage}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await resp.json()

      data.photos.forEach(photo => {
        if (!photo.is_image) return
        this._appendPhoto(photo)
      })

      this._isLastPage = data.is_last_page
      this._photosLoaded = true
      this._currentPage++

      // Load all pages
      if (!this._isLastPage) {
        this._loadingPhotos = false
        this._loadPhotos()
        return
      }
    } catch (e) {
      console.error("Failed to load photos:", e)
    } finally {
      this._loadingPhotos = false
      this.photoSpinnerTarget.classList.add("hidden")
    }
  }

  _appendPhoto(photo) {
    const div = document.createElement("label")
    div.className = "relative cursor-pointer group"
    div.innerHTML = `
      <input type="checkbox" value="${photo.id}" class="sr-only peer" data-action="change->customer-report#updateCount">
      <div class="w-full aspect-square rounded-lg overflow-hidden border-2 border-transparent peer-checked:border-blue-500 peer-checked:ring-2 peer-checked:ring-blue-200 transition-all bg-gray-100 animate-pulse">
        <img src="${photo.thumbnail_url}" alt="" class="w-full h-full object-cover opacity-0 transition-opacity duration-300" loading="lazy"
             data-controller="img-handler"
             data-action="load->img-handler#showLoadedAndStopPulse error->img-handler#greyOutAndRemove">
      </div>
      <div class="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-white/80 border border-gray-300 flex items-center justify-center opacity-0 peer-checked:opacity-100 transition-opacity">
        <svg class="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
      </div>
    `
    if (photo.comment_text) {
      const caption = document.createElement("p")
      caption.className = "text-[13px] text-gray-500 truncate mt-1"
      caption.textContent = photo.comment_text
      div.appendChild(caption)
    }
    this.photoGridTarget.appendChild(div)
  }

  updateCount() {
    const checked = this.photoGridTarget.querySelectorAll("input:checked")
    this.photoCountTarget.textContent = `${checked.length} photo${checked.length !== 1 ? "s" : ""} selected`
  }

  selectAll() {
    const boxes = this.photoGridTarget.querySelectorAll("input[type=checkbox]")
    const allChecked = Array.from(boxes).every(cb => cb.checked)
    boxes.forEach(cb => { cb.checked = !allChecked })
    this.updateCount()
  }

  // ── Submit ────────────────────────────────────────────────────────

  async _submit() {
    // Collect selected photo IDs
    const checked = this.photoGridTarget.querySelectorAll("input:checked")
    const photoIds = Array.from(checked).map(cb => cb.value).join(",")

    // Build form data from step 1 inputs
    const formData = new FormData()

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrf) formData.append("authenticity_token", csrf)

    const inputs = this.step1Target.querySelectorAll("input[name], select[name], textarea[name]")
    inputs.forEach(input => {
      if (input.type === "checkbox") {
        if (input.checked) formData.append(input.name, "on")
        return
      }
      formData.append(input.name, input.value)
    })

    formData.append("selected_photos", photoIds)

    // Disable button while submitting
    this.nextBtnTarget.disabled = true
    this.nextBtnTarget.textContent = "Sending..."

    try {
      const resp = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
        body: formData
      })
      const data = await resp.json()

      this._close()
      this._showGeneratingToast("Generating report. Check below in a moment.")

      // Dispatch event so reports controller inserts the row
      if (data.report) {
        document.dispatchEvent(new CustomEvent("customer-report:created", {
          detail: { report: data.report }
        }))

        // Start polling for completion
        if (data.report.poll_url) {
          this._pollForCompletion(data.report.id, data.report.poll_url)
        }
      }
    } catch (e) {
      console.error("Customer report submit failed:", e)
      this._showToast("Failed to generate report. Please try again.", "alert")
    } finally {
      this.nextBtnTarget.disabled = false
      this.nextBtnTarget.textContent = "Create"
    }
  }

  // ── Polling ─────────────────────────────────────────────────────────

  _pollForCompletion(reportId, pollUrl) {
    const poll = async () => {
      try {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content
        const resp = await fetch(pollUrl, {
          headers: { "Accept": "application/json", "X-CSRF-Token": csrf },
          credentials: "same-origin"
        })
        const data = await resp.json()

        if (data.processing === 0) {
          // Report is ready
          document.dispatchEvent(new CustomEvent("customer-report:ready", {
            detail: { reportId: String(reportId) }
          }))
          this._hideGeneratingToast()
          this._showToast("Customer report is ready.", "notice")
          return
        }

        // Not ready yet, poll again in 3 seconds
        const timer = setTimeout(poll, 3000)
        this._pollTimers.push(timer)
      } catch (e) {
        console.error("Poll failed:", e)
        // Retry after longer delay on error
        const timer = setTimeout(poll, 5000)
        this._pollTimers.push(timer)
      }
    }

    const timer = setTimeout(poll, 3000)
    this._pollTimers.push(timer)
  }

  _showGeneratingToast(message) {
    this._hideGeneratingToast()
    const el = document.createElement("div")
    el.id = "customer-report-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideGeneratingToast() {
    const existing = document.getElementById("customer-report-generating-toast")
    if (existing) existing.remove()
  }

  _showToast(message, type) {
    const cssType = (type === "alert" || type === "error") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
